import { BrowserRouter, Navigate, Route, Routes, Outlet, useLocation } from 'react-router-dom';
import AppLayout from './layout/AppLayout';
import Dashboard from './pages/Dashboard';
import ClinicianDashboard from './pages/ClinicianDashboard';
import Patients from './pages/Patients';
import PatientDetailPage from './pages/PatientDetailPage';
import Appointments from './pages/Appointments';
import TreatmentCompletionPage from './pages/TreatmentCompletionPage';
import VisitWorkspacePage from './pages/VisitWorkspacePage';
import ExternalPaymentRegistrationPage from './pages/ExternalPaymentRegistrationPage';
import PromotionsPage from './pages/PromotionsPage';
import PackagesPage from './pages/PackagesPage';
import OffersPackagesPage from './pages/OffersPackagesPage';
import MarketingCampaignsPage from './pages/MarketingCampaignsPage';
import LeadsPage from './pages/LeadsPage';
import OperationalReportsPage from './pages/OperationalReportsPage';
import FinancialReportsPage from './pages/FinancialReportsPage';
import AuditActivityPage from './pages/AuditActivityPage';
import AdvancedInventoryPage from './pages/AdvancedInventoryPage';
import LoyaltyPage from './pages/LoyaltyPage';
import PosPaymentsPage from './pages/PosPaymentsPage';
import StaffOperationsPage from './pages/StaffOperationsPage';
import TimesheetsPage from './pages/TimesheetsPage';
import MobileCapturePage from './pages/MobileCapturePage';
import Telemed from './pages/Telemed';
import Billing from './pages/Billing';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import SettingsProfile from './pages/settings/SettingsProfile';
import SettingsLocations from './pages/settings/SettingsLocations';
import SettingsUsers from './pages/settings/SettingsUsers';
import SettingsRoles from './pages/settings/SettingsRoles';
import SettingsProviders from './pages/settings/SettingsProviders';
import SettingsProviderDetail from './pages/settings/SettingsProviderDetail';
import SettingsBillingCatalog from './pages/settings/SettingsBillingCatalog';
import SettingsResources, { SettingsPracticeUsers, SettingsScheduleAssets } from './pages/settings/SettingsResources';
import SettingsRoomManagement from './pages/settings/SettingsRoomManagement';
import SettingsScheduling from './pages/settings/SettingsScheduling';
import SettingsTelemed from './pages/settings/SettingsTelemed';
import SettingsPayments from './pages/settings/SettingsPayments';
import SettingsMedicalForms from './pages/settings/SettingsMedicalForms';
import SettingsCharts from './pages/settings/SettingsCharts';
import SettingsExternalUsers from './pages/settings/SettingsExternalUsers';
import SettingsMyAccount from './pages/settings/SettingsMyAccount';
import SettingsAppointmentTypes from './pages/settings/SettingsAppointmentTypes';
import SettingsManualTests from './pages/settings/SettingsManualTests';
import SettingsManualTestEditor from './pages/settings/SettingsManualTestEditor';
import SettingsTreatmentPlans from './pages/settings/SettingsTreatmentPlans';
import SettingsDocumentTypes from './pages/settings/SettingsDocumentTypes';
import Messages from './pages/Messages';
import { RootRedirectHandler } from './components/RootRedirectHandler';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { installNotificationsFetchBridge } from './lib/notificationsBootstrap';
import TelemedRoom from './pages/TelemedRoom';
import CoreClinicalPage from './pages/coreclinical/CoreClinicalPage';
import RoomManagement from './pages/RoomManagement';
import RevenueInsightsDashboard from './pages/RevenueInsightsDashboard';
import GenomicDataIntegration from './pages/GenomicDataIntegration';
import AdaptiveAISystems from './pages/AdaptiveAISystems';
import UniversalSearchPage from './pages/UniversalSearchPage';
import ClaimsRetryEnginePage from './pages/ClaimsRetryEnginePage';
import BillingRulesEnginePage from './pages/BillingRulesEnginePage';
import SubscriptionMembershipPage from './pages/SubscriptionMembershipPage';
import AsynchronousVisitsPage from './pages/AsynchronousVisitsPage';
import AiCallSummariesPage from './pages/AiCallSummariesPage';
import InventoryAutoReorderPage from './pages/InventoryAutoReorderPage';
import PharmacyDispensingPage from './pages/PharmacyDispensingPage';
import TechnologyPage from './features/landing/TechnologyPage';
import InvestorsPage from './features/landing/InvestorsPage';
import PatientAppPage from './features/landing/PatientAppPage';
import LandingPage from './features/landing/LandingPage';
import AiEmrPage from './features/landing/AiEmrPage';
import TriageAiPage from './features/landing/TriageAiPage';
import HealthcareAnalyticsPage from './features/landing/HealthcareAnalyticsPage';
import LandingDetailPage from './features/landing/LandingDetailPage';
import { LANDING_SUBPAGE_KEYS, LANDING_SUBPAGE_PATHS } from './features/landing/siteContent';
import PublicMedicalFormPage from './pages/PublicMedicalFormPage';
import PublicBookingPage from './pages/PublicBookingPage';
import DesignPrototypePage from './pages/DesignPrototypePage';
import { LanguageProvider, useI18n } from './i18n/LanguageContext';
import { UiTextLocalizationBridge } from './i18n/UiTextLocalizationBridge';
import { setOrgFormat } from './i18n/orgFormat';
import { SentinelProvider } from './features/sentinel/SentinelProvider';
import { API_BASE, AUTH_AUDIENCE, PATIENT_PORTAL_ENABLED, PATIENT_PORTAL_URL } from './config/env';
import { SHOW_DEVELOPMENT_PREVIEWS } from './config/developmentPreviews';
import { AuthLoginPage } from './auth/client';

const GA_TRACKING_ID = 'G-K9W9TDSNFY';

const LANDING_MARKET_PREFIXES = ['', '/es'] as const;
const LEGACY_PUBLIC_PATHS = ['/patient-app', '/technology', '/investors', '/ai-emr', '/triage-ai', '/healthcare-analytics'] as const;

function escapeRegex(path: string) {
  return path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const PUBLIC_ROUTE_PATTERNS = [
  /^\/$/,
  ...LANDING_SUBPAGE_PATHS.flatMap((path) =>
    LANDING_MARKET_PREFIXES.map((prefix) => new RegExp(`^${escapeRegex(prefix)}${escapeRegex(path)}$`)),
  ),
  ...LEGACY_PUBLIC_PATHS.flatMap((path) =>
    LANDING_MARKET_PREFIXES.map((prefix) => new RegExp(`^${escapeRegex(prefix)}${escapeRegex(path)}$`)),
  ),
  /^\/es(?:\/)?$/,
  /^\/forms\/f\/[^/]+$/,
  /^\/book\/[^/]+$/,
];

function isPublicRoute(pathname: string) {
  return PUBLIC_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
}

function trackPublicPageView(path: string) {
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== 'function') return;
  gtag('config', GA_TRACKING_ID, { page_path: path });
}

// Mirrors the backend tenant feature-flag set returned by GET /users/me
// (see backend/src/lib/tenant-feature-flags.ts). Keep in sync when flags change.
type StaffFeatureFlags = {
  populationHealthEnabled?: boolean;
  publicInstitutionIntegrationsEnabled?: boolean;
  treatmentCatalogEnabled?: boolean;
  radiologyCoreEnabled?: boolean;
  dicomIngestEnabled?: boolean;
  pacsImportEnabled?: boolean;
  aiTriageEnabled?: boolean;
  aiInterpretationEnabled?: boolean;
  aiReportDraftEnabled?: boolean;
  aiExplainabilityEnabled?: boolean;
  radiologyViewerEnabled?: boolean;
  radiologyAnalyticsEnabled?: boolean;
  radiologyCommsEnabled?: boolean;
  radiologyFhirSyncEnabled?: boolean;
  radiologyEdgeSupportEnabled?: boolean;
  fseCoreEnabled?: boolean;
  fseDocumentsEnabled?: boolean;
  fsePatientDocumentAutoSyncEnabled?: boolean;
  fseFhirValidationEnabled?: boolean;
  fseGatewayEnabled?: boolean;
  fseRegionalAdaptersEnabled?: boolean;
};

function Protected({
  children,
  requiredFeatureFlag,
  featureFlagRedirectPath = '/appointments',
  adminOnly = false,
}: {
  children: React.ReactNode;
  requiredFeatureFlag?: keyof StaffFeatureFlags;
  featureFlagRedirectPath?: string;
  adminOnly?: boolean;
}) {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout, error, user } = useAuth0();
  const { tr } = useI18n();
  const location = useLocation();
  const [gateLoading, setGateLoading] = useState(false);
  const [userRoles, setUserRoles] = useState<string[] | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState<boolean | null>(null);
  const [featureFlags, setFeatureFlags] = useState<StaffFeatureFlags | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } }).catch(() => { });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setGateLoading(false);
      setUserRoles(null);
      setMustChangePassword(null);
      setFeatureFlags(null);
      return;
    }
    let active = true;
    setGateLoading(true);
    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: AUTH_AUDIENCE },
        });
        const meResponse = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let roles: string[] = [];
        let forcePasswordChange = false;
        if (meResponse.ok) {
          const payload = await meResponse.json();
          // Set process-wide money formatting from the org context (locale + currency).
          setOrgFormat({
            language: payload?.organization?.effectiveLanguage,
            country: payload?.organization?.country,
            currency: payload?.organization?.defaultCurrency,
          });
          const fromApi = Array.isArray(payload?.roles) ? payload.roles : [payload?.role];
          roles = fromApi
            .map((entry) => String(entry || '').toUpperCase())
            .filter(Boolean);
          forcePasswordChange = Boolean(payload?.mustChangePassword);
          if (active) {
            setFeatureFlags(
              payload?.featureFlags && typeof payload.featureFlags === 'object'
                ? payload.featureFlags
                : {},
            );
          }
        }
        if (active) {
          setUserRoles(roles);
          setMustChangePassword(forcePasswordChange);
          if (!meResponse.ok) setFeatureFlags({});
        }
      } catch {
        if (active) {
          setUserRoles([]);
          setMustChangePassword(false);
          setFeatureFlags({});
        }
      } finally {
        if (active) setGateLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading, location.pathname]);

  if (isLoading) return <div className="p-6">{tr('Loading...')}</div>;
  if (error) return <div className="p-6 text-rose-600">{tr('Auth error')}: {error.message}</div>;
  if (!isAuthenticated) return <div className="p-6">{tr('Redirecting to login...')}</div>;
  if (gateLoading || userRoles === null || mustChangePassword === null || featureFlags === null) return <div className="p-6">{tr('Loading...')}</div>;
  const roleSet = new Set(userRoles.map((entry) => entry.toUpperCase()));
  const isPatient = roleSet.has('PATIENT');
  const isAdmin = roleSet.has('ADMIN');
  if (isPatient) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-2xl flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-2xl font-semibold text-rose-700">{tr('Access denied')}</h1>
        <p className="text-slate-700">
          {tr('Patient accounts cannot access the main staff application. Please use the patient portal instead.')}
        </p>
        <p className="text-sm text-slate-600">
          {tr('Signed in as')} <strong>{user?.email || tr('unknown account')}</strong>
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
            onClick={() =>
              loginWithRedirect({
                authorizationParams: { audience: AUTH_AUDIENCE, prompt: 'login' },
                appState: { returnTo: '/appointments' },
              }).catch(() => { })
            }
          >
            {tr('Switch account')}
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 hover:bg-slate-50"
            onClick={() =>
              logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          >
            {tr('Log out')}
          </button>
        </div>
      </div>
    );
  }
  if (location.pathname === '/portal') return <Navigate to="/appointments" replace />;
  if (!isAdmin && location.pathname === '/command-center') return <Navigate to="/appointments" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (mustChangePassword) {
    if (location.pathname !== '/settings/account' || !location.search.includes('forcePasswordChange=1')) {
      return <Navigate to="/settings/account?forcePasswordChange=1" replace />;
    }
  }
  if (requiredFeatureFlag && !featureFlags[requiredFeatureFlag]) {
    return <Navigate to={featureFlagRedirectPath} replace />;
  }
  return <>{children}</>;
}

function Layout() {
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function PublicRouteAnalytics() {
  const location = useLocation();

  useEffect(() => {
    if (!isPublicRoute(location.pathname)) return;
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    trackPublicPageView(pagePath);
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function PortalRedirectPage() {
  useEffect(() => {
    if (PATIENT_PORTAL_ENABLED && PATIENT_PORTAL_URL) {
      window.location.assign(PATIENT_PORTAL_URL);
    }
  }, []);

  if (!PATIENT_PORTAL_ENABLED || !PATIENT_PORTAL_URL) {
    return <div className="p-6 text-amber-700">Patient portal is currently disabled.</div>;
  }

  return <div className="p-6">Redirecting to patient portal...</div>;
}

function AppShell() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirectHandler />} />
      <Route path="/v2" element={<Navigate to="/" replace />} />
      <Route path="/patient-app" element={<PatientAppPage />} />
      <Route path="/technology" element={<TechnologyPage />} />
      <Route path="/investors" element={<InvestorsPage />} />
      <Route path="/ai-emr" element={<AiEmrPage />} />
      <Route path="/triage-ai" element={<TriageAiPage />} />
      <Route path="/healthcare-analytics" element={<HealthcareAnalyticsPage />} />
      {LANDING_SUBPAGE_KEYS.map((key, index) => (
        <Route key={LANDING_SUBPAGE_PATHS[index]} path={LANDING_SUBPAGE_PATHS[index]} element={<LandingDetailPage pageKey={key} />} />
      ))}
      <Route
        path="/login"
        element={
          <AuthLoginPage variant="staff" />
        }
      />
      <Route path="/es" element={<LandingPage />} />
      <Route path="/es/patient-app" element={<PatientAppPage />} />
      <Route path="/es/technology" element={<TechnologyPage />} />
      <Route path="/es/investors" element={<InvestorsPage />} />
      <Route path="/es/ai-emr" element={<AiEmrPage />} />
      <Route path="/es/triage-ai" element={<TriageAiPage />} />
      <Route path="/es/healthcare-analytics" element={<HealthcareAnalyticsPage />} />
      {LANDING_MARKET_PREFIXES.filter((prefix) => prefix !== '').flatMap((prefix) =>
        LANDING_SUBPAGE_KEYS.map((key, index) => (
          <Route
            key={`${prefix}${LANDING_SUBPAGE_PATHS[index]}`}
            path={`${prefix}${LANDING_SUBPAGE_PATHS[index]}`}
            element={<LandingDetailPage pageKey={key} />}
          />
        )),
      )}
      <Route path="/forms/f/:token" element={<PublicMedicalFormPage />} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route
        path="/portal"
        element={
          <Protected>
            <PortalRedirectPage />
          </Protected>
        }
      />
      {/* Design-system prototype (reference only). Dev-gated like other preview
          pages; renders its own self-contained "Calm precision" shell so it is
          intentionally NOT wrapped in the app's AppLayout. Not in any nav.
          NOTE: intentionally NOT wrapped in <Protected> so the mock-only preview
          can be viewed in `npm run dev` without a backend/login. In production
          SHOW_DEVELOPMENT_PREVIEWS is false, so it redirects to /dashboard. */}
      <Route
        path="/design-prototype"
        element={SHOW_DEVELOPMENT_PREVIEWS ? <DesignPrototypePage /> : <Navigate to="/dashboard" replace />}
      />

      <Route element={<Layout />}>
        <Route
          path="/dashboard"
          element={
            <Protected>
              <Dashboard />
            </Protected>
          }
        />
        <Route
          path="/command-center"
          element={
            <Protected>
              <Dashboard />
            </Protected>
          }
        />
        <Route
          path="/patients"
          element={
            <Protected>
              <Patients />
            </Protected>
          }
        />
        <Route
          path="/patients/:id"
          element={
            <Protected>
              <PatientDetailPage />
            </Protected>
          }
        />
        <Route
          path="/today"
          element={
            <Protected>
              <Navigate to="/appointments" replace />
            </Protected>
          }
        />
        <Route
          path="/appointments"
          element={
            <Protected>
              <Appointments />
            </Protected>
          }
        />
        <Route
          path="/appointments/:id/complete"
          element={
            <Protected requiredFeatureFlag="treatmentCatalogEnabled" featureFlagRedirectPath="/appointments">
              <TreatmentCompletionPage />
            </Protected>
          }
        />
        <Route
          path="/visits/:id"
          element={
            <Protected>
              <VisitWorkspacePage />
            </Protected>
          }
        />
        <Route
          path="/appointments/:id/payment"
          element={
            <Protected>
              <ExternalPaymentRegistrationPage />
            </Protected>
          }
        />
        <Route
          path="/offers"
          element={
            <Protected>
              <OffersPackagesPage />
            </Protected>
          }
        />
        <Route
          path="/promotions"
          element={
            <Protected>
              <PromotionsPage />
            </Protected>
          }
        />
        <Route
          path="/packages"
          element={
            <Protected>
              <PackagesPage />
            </Protected>
          }
        />
        <Route
          path="/marketing"
          element={
            <Protected>
              <MarketingCampaignsPage />
            </Protected>
          }
        />
        <Route
          path="/leads"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <LeadsPage /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/reports/operations"
          element={
            <Protected>
              <OperationalReportsPage />
            </Protected>
          }
        />
        <Route
          path="/reports/financial"
          element={
            <Protected>
              <FinancialReportsPage />
            </Protected>
          }
        />
        <Route
          path="/audit/activity"
          element={
            <Protected>
              <AuditActivityPage />
            </Protected>
          }
        />
        <Route
          path="/inventory"
          element={
            <Protected>
              <InventoryAutoReorderPage mode="operations" />
            </Protected>
          }
        />
        <Route
          path="/inventory/advanced"
          element={
            <Protected>
              <AdvancedInventoryPage />
            </Protected>
          }
        />
        <Route
          path="/loyalty"
          element={
            <Protected>
              <LoyaltyPage />
            </Protected>
          }
        />
        <Route
          path="/pos"
          element={
            <Protected>
              <PosPaymentsPage />
            </Protected>
          }
        />
        <Route
          path="/staff-operations"
          element={
            <Protected>
              <StaffOperationsPage />
            </Protected>
          }
        />
        <Route
          path="/timesheets"
          element={
            <Protected>
              <TimesheetsPage />
            </Protected>
          }
        />
        <Route
          path="/mobile-capture"
          element={
            <Protected>
              <MobileCapturePage />
            </Protected>
          }
        />
        <Route
          path="/messages"
          element={
            <Protected>
              <Messages />
            </Protected>
          }
        />
        <Route
          path="/telemedicine"
          element={
            <Protected>
              <Telemed />
            </Protected>
          }
        />
        <Route
          path="/universal-search"
          element={
            <Protected>
              <UniversalSearchPage />
            </Protected>
          }
        />
        <Route
          path="/claims-retry-engine"
          element={
            <Protected>
              <ClaimsRetryEnginePage />
            </Protected>
          }
        />
        <Route
          path="/billing-rules-engine"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <BillingRulesEnginePage /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/subscription-membership"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <SubscriptionMembershipPage /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/revenue-insights"
          element={
            <Protected>
              <RevenueInsightsDashboard />
            </Protected>
          }
        />
        <Route
          path="/genomic-data-integration"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <GenomicDataIntegration /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/adaptive-ai-systems"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <AdaptiveAISystems /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/asynchronous-visits"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <AsynchronousVisitsPage /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/ai-call-summaries"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <AiCallSummariesPage /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/telemedicine/:id"
          element={
            <Protected>
              <TelemedRoom />
            </Protected>
          }
        />
        <Route
          path="/inventory-auto-reorder"
          element={
            <Protected>
              <InventoryAutoReorderPage />
            </Protected>
          }
        />
        <Route
          path="/pharmacy-dispensing"
          element={
            <Protected>
              {SHOW_DEVELOPMENT_PREVIEWS ? <PharmacyDispensingPage /> : <Navigate to="/dashboard" replace />}
            </Protected>
          }
        />
        <Route
          path="/billing"
          element={
            <Protected>
              <Billing />
            </Protected>
          }
        />
        <Route
          path="/reports"
          element={
            <Protected>
              <Reports />
            </Protected>
          }
        />
        <Route
          path="/core-clinical/:slug"
          element={
            <Protected>
              <CoreClinicalPage />
            </Protected>
          }
        />
        <Route
          path="/room-management"
          element={
            <Protected>
              <RoomManagement />
            </Protected>
          }
        />
        <Route
          path="/settings"
          element={
            <Protected>
              <Settings />
            </Protected>
          }
        />
        <Route path="/settings/profile" element={<Protected><SettingsProfile /></Protected>} />
        <Route path="/settings/locations" element={<Protected adminOnly><SettingsLocations /></Protected>} />
        <Route path="/settings/locations/new" element={<Protected adminOnly><SettingsLocations /></Protected>} />
        <Route path="/settings/locations/:locationId" element={<Protected adminOnly><SettingsLocations /></Protected>} />
        <Route path="/settings/users" element={<Protected adminOnly><SettingsUsers /></Protected>} />
        <Route path="/settings/users/:userId" element={<Protected adminOnly><SettingsUsers /></Protected>} />
        <Route path="/settings/roles" element={<Protected adminOnly><SettingsRoles /></Protected>} />
        <Route path="/settings/roles/:roleId" element={<Protected adminOnly><SettingsRoles /></Protected>} />
        <Route path="/settings/providers" element={<Protected><SettingsProviders /></Protected>} />
        <Route path="/settings/providers/:providerId" element={<Protected><SettingsProviderDetail /></Protected>} />
        <Route path="/settings/billing-catalog" element={<Protected><SettingsBillingCatalog /></Protected>} />
        <Route path="/settings/inventory" element={<Protected><InventoryAutoReorderPage mode="setup" /></Protected>} />
        <Route path="/settings/resources" element={<Protected><SettingsResources /></Protected>} />
        <Route path="/settings/practice-users" element={<Protected><SettingsPracticeUsers /></Protected>} />
        <Route path="/settings/schedule-assets" element={<Protected><SettingsScheduleAssets /></Protected>} />
        <Route path="/settings/room-management" element={<Protected><SettingsRoomManagement /></Protected>} />
        <Route path="/settings/appointmenttypes" element={<Protected><SettingsAppointmentTypes /></Protected>} />
        <Route path="/settings/appointmenttypes/new" element={<Protected><SettingsAppointmentTypes /></Protected>} />
        <Route path="/settings/appointmenttypes/:appointmentTypeId" element={<Protected><SettingsAppointmentTypes /></Protected>} />
        <Route path="/settings/scheduling" element={<Protected><SettingsScheduling /></Protected>} />
        <Route path="/settings/telemed" element={<Protected><SettingsTelemed /></Protected>} />
        <Route path="/settings/payments" element={<Protected><SettingsPayments /></Protected>} />
        <Route path="/settings/manual-tests" element={<Protected><SettingsManualTests /></Protected>} />
        <Route path="/settings/manual-tests/new" element={<Protected><SettingsManualTestEditor /></Protected>} />
        <Route path="/settings/manual-tests/:testCode" element={<Protected><SettingsManualTestEditor /></Protected>} />
        <Route path="/settings/treatment-plans" element={<Protected><SettingsTreatmentPlans /></Protected>} />
        <Route path="/settings/medical-forms" element={<Protected><SettingsMedicalForms /></Protected>} />
        <Route path="/settings/charts" element={<Protected><SettingsCharts /></Protected>} />
        <Route path="/settings/external-users" element={<Protected><SettingsExternalUsers /></Protected>} />
        <Route path="/settings/document-types" element={<Protected><SettingsDocumentTypes /></Protected>} />
        <Route path="/settings/document-types/new" element={<Protected><SettingsDocumentTypes /></Protected>} />
        <Route path="/settings/document-types/:documentTypeId" element={<Protected><SettingsDocumentTypes /></Protected>} />
        <Route path="/settings/account" element={<Protected><SettingsMyAccount /></Protected>} />

      </Route>
      <Route path="*" element={<Navigate to="/appointments" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    installNotificationsFetchBridge();
  }, []);

  return (
    <LanguageProvider>
      <UiTextLocalizationBridge />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SentinelProvider>
          <PublicRouteAnalytics />
          <AppShell />
        </SentinelProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
